/**
 * Search controls for the Sales Comps module.
 * County dropdown, property type checkboxes, date range, price filters.
 */

import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import type { CountyInfo, PropertyTypeInfo, CompSearchRequest } from "../../api/comps";
import { fetchCounties, fetchPropertyTypes } from "../../api/comps";

const PROPERTY_TYPE_BADGE: Record<string, string> = {
  Retail: "bg-green-100 text-green-800 border-green-300",
  Office: "bg-blue-100 text-blue-800 border-blue-300",
  Industrial: "bg-amber-100 text-amber-800 border-amber-300",
  Hospitality: "bg-purple-100 text-purple-800 border-purple-300",
  "Special Purpose": "bg-gray-100 text-gray-700 border-gray-300",
  Commercial: "bg-teal-100 text-teal-800 border-teal-300",
};

interface CompsSearchBarProps {
  hasGeofence: boolean;
  onSearch: (req: CompSearchRequest) => void;
  onClear: () => void;
  loading: boolean;
}

export default function CompsSearchBar({
  hasGeofence,
  onSearch,
  onClear,
  loading,
}: CompsSearchBarProps) {
  const [counties, setCounties] = useState<CountyInfo[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<PropertyTypeInfo[]>([]);
  const [county, setCounty] = useState("Miami-Dade");
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [qualifiedOnly, setQualifiedOnly] = useState(true);
  const [excludeCondos, setExcludeCondos] = useState(false);
  const [minBuildingSf, setMinBuildingSf] = useState("");
  const [maxBuildingSf, setMaxBuildingSf] = useState("");
  const [minYearBuilt, setMinYearBuilt] = useState("");
  const [maxYearBuilt, setMaxYearBuilt] = useState("");
  const [minPricePsf, setMinPricePsf] = useState("");
  const [maxPricePsf, setMaxPricePsf] = useState("");
  // rings are stored in parent; we get hasGeofence as signal

  useEffect(() => {
    fetchCounties().then(setCounties).catch(() => {});
    fetchPropertyTypes().then(setPropertyTypes).catch(() => {});
  }, []);

  // Default date range: 24 months ago → today
  useEffect(() => {
    const now = new Date();
    const twoYearsAgo = new Date(now);
    twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);
    setDateEnd(now.toISOString().slice(0, 10));
    setDateStart(twoYearsAgo.toISOString().slice(0, 10));
  }, []);

  const toggleType = (name: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const canSearch = hasGeofence && selectedTypes.size > 0 && !loading;

  const handleSearch = () => {
    if (!canSearch) return;
    // rings will be injected by parent CompsPage from the map
    onSearch({
      geometry: { rings: [] }, // placeholder — parent fills this in
      county,
      property_types: Array.from(selectedTypes),
      date_start: dateStart || null,
      date_end: dateEnd || null,
      min_price: minPrice ? parseFloat(minPrice) : null,
      max_price: maxPrice ? parseFloat(maxPrice) : null,
      qualified_only: qualifiedOnly,
      exclude_condos: excludeCondos,
      min_building_sf: minBuildingSf ? parseFloat(minBuildingSf) : null,
      max_building_sf: maxBuildingSf ? parseFloat(maxBuildingSf) : null,
      min_year_built: minYearBuilt ? parseInt(minYearBuilt) : null,
      max_year_built: maxYearBuilt ? parseInt(maxYearBuilt) : null,
      min_price_psf: minPricePsf ? parseFloat(minPricePsf) : null,
      max_price_psf: maxPricePsf ? parseFloat(maxPricePsf) : null,
    });
  };

  const handleClear = () => {
    setSelectedTypes(new Set());
    setMinPrice("");
    setMaxPrice("");
    setExcludeCondos(false);
    setMinBuildingSf("");
    setMaxBuildingSf("");
    setMinYearBuilt("");
    setMaxYearBuilt("");
    setMinPricePsf("");
    setMaxPricePsf("");
    onClear();
  };

  return (
    <div className="bg-white rounded-lg border border-grove-300 p-4 space-y-3">
      {/* County dropdown */}
      <div>
        <label className="block text-xs font-medium text-bark-500 mb-1">County</label>
        <select
          value={county}
          onChange={(e) => setCounty(e.target.value)}
          className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-forest-500"
        >
          {counties.map((c) => (
            <option key={c.name} value={c.name} disabled={!c.active}>
              {c.name} {!c.active ? "(Coming Soon)" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Property type checkboxes */}
      <div>
        <label className="block text-xs font-medium text-bark-500 mb-1">
          Property Type
        </label>
        <div className="flex flex-wrap gap-2">
          {propertyTypes.map((pt) => {
            const isSelected = selectedTypes.has(pt.name);
            const badgeClass = PROPERTY_TYPE_BADGE[pt.name] || "bg-gray-100 text-gray-700";
            return (
              <button
                key={pt.name}
                onClick={() => toggleType(pt.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  isSelected
                    ? badgeClass + " ring-2 ring-offset-1 ring-forest-500"
                    : "bg-grove-100 text-bark-400 border-grove-300 hover:bg-grove-200"
                }`}
              >
                {pt.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Date range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
      </div>

      {/* Price range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Min Price
          </label>
          <input
            type="number"
            placeholder="$0"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Max Price
          </label>
          <input
            type="number"
            placeholder="No max"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
      </div>

      {/* Building SF range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Min Bldg SF
          </label>
          <input
            type="number"
            placeholder="0"
            value={minBuildingSf}
            onChange={(e) => setMinBuildingSf(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Max Bldg SF
          </label>
          <input
            type="number"
            placeholder="No max"
            value={maxBuildingSf}
            onChange={(e) => setMaxBuildingSf(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
      </div>

      {/* Year Built range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Min Year Built
          </label>
          <input
            type="number"
            placeholder="e.g. 1990"
            value={minYearBuilt}
            onChange={(e) => setMinYearBuilt(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Max Year Built
          </label>
          <input
            type="number"
            placeholder="No max"
            value={maxYearBuilt}
            onChange={(e) => setMaxYearBuilt(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
      </div>

      {/* Price PSF range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Min $/SF
          </label>
          <input
            type="number"
            placeholder="$0"
            value={minPricePsf}
            onChange={(e) => setMinPricePsf(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-bark-500 mb-1">
            Max $/SF
          </label>
          <input
            type="number"
            placeholder="No max"
            value={maxPricePsf}
            onChange={(e) => setMaxPricePsf(e.target.value)}
            className="w-full px-2 py-1.5 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-bark-600 cursor-pointer">
          <input
            type="checkbox"
            checked={qualifiedOnly}
            onChange={(e) => setQualifiedOnly(e.target.checked)}
            className="rounded border-grove-300 text-forest-700 focus:ring-forest-500"
          />
          Qualified sales only
        </label>
        <label className="flex items-center gap-2 text-sm text-bark-600 cursor-pointer">
          <input
            type="checkbox"
            checked={excludeCondos}
            onChange={(e) => setExcludeCondos(e.target.checked)}
            className="rounded border-grove-300 text-forest-700 focus:ring-forest-500"
          />
          Exclude condos
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSearch}
          disabled={!canSearch}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-forest-700 text-white text-sm font-medium rounded-lg hover:bg-forest-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Search className="w-4 h-4" />
          {loading ? "Searching..." : "Search"}
        </button>
        <button
          onClick={handleClear}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-bark-500 border border-grove-300 rounded-lg hover:bg-grove-100 transition-colors"
        >
          <X className="w-4 h-4" />
          Clear
        </button>
      </div>

      {!hasGeofence && (
        <p className="text-xs text-bark-400 text-center">
          Draw a polygon or rectangle on the map to define your search area
        </p>
      )}
    </div>
  );
}
